import React, { useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import OrderProcessingStatus from './components/OrderProcessingStatus';
import ProcuredSection from './components/ProcuredSection';
import CancelledItemsSection from './components/CancelledItemsSection';
import WorkplaceShippingSection from './components/WorkplaceShippingSection';
import FacilitySection from './components/FacilitySection';
import AfterShipmentSection from './components/AfterShipmentSection';
import { useDashboardStore } from '../../stores/dashboard-store';
import { useStoreStore } from '../../stores/store-store';
import { useLanguage } from '../../contexts/LanguageContext';

const DashboardPage: React.FC = () => {
  const { isLoading, error, fetchStats, clearError } = useDashboardStore();
  const { currentStore, isLoading: storesLoading } = useStoreStore();
  const { t } = useLanguage();

  useEffect(() => {
    // Fetch stats when currentStore changes
    if (currentStore) {
      fetchStats();
    }
  }, [currentStore, fetchStats]);

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <div className="h-5 w-5 text-red-400">⚠</div>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{t('common.errorLoading')} dashboard</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <div className="mt-4">
                <button
                  onClick={() => {
                    clearError();
                    fetchStats();
                  }}
                  className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
                >
                  {t('common.tryAgain')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="bg-gray-50 min-h-screen p-4 md:p-5 lg:p-6">

      {/* Loading State */}
      {(isLoading || storesLoading) && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
          <span className="ml-2 text-gray-600">
            {storesLoading ? t('common.loadingStores') : t('common.loadingDashboardData')}
          </span>
        </div>
      )}

      {/* Dashboard Content - Using existing components */}
      {!currentStore ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="text-gray-500 text-lg mb-2">{t('common.noStoreSelected')}</div>
            <div className="text-gray-400 text-sm">{t('common.pleaseSelectStore')}</div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <OrderProcessingStatus />
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ProcuredSection />
            <CancelledItemsSection />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <WorkplaceShippingSection />
            <FacilitySection />
          </div>

          <AfterShipmentSection />
        </div>
      )}
      </div>
    </ErrorBoundary>
  );
};

export default DashboardPage;